// 🔑 SSOT ID Canonical 유틸리티 - 단일 진실 소스
// [배경] 모델/가격 ID의 형식 혼재로 인한 중복 생성 문제를 완전 해결
// [원칙] Read는 레거시 허용 / Write는 canonical만 / 비교는 canonicalize 후에만
//
// Canonical 규칙:
// - modelId: {brand}-{deviceType}-{series}-{modelEn}
// - priceId: {modelId}-{storage?}-{connectivity?}-{region}
// - region: 항상 'kr' (소문자)
// - 스마트폰: connectivity = 'lte' 강제
// - 태블릿: connectivity = 'wifi' | 'cellular' 필수
// - 웨어러블: storage/connectivity 포함 금지

export type DeviceType = 'smartphone' | 'tablet' | 'wearable';
export type Connectivity = 'lte' | 'wifi' | 'cellular';
export type Region = 'kr';

// ============================================================================
// 🔑 상수 및 패턴
// ============================================================================

const BRANDS = ['apple', 'samsung'] as const;
const DEVICE_TYPES = ['smartphone', 'tablet', 'wearable'] as const;

// storage 패턴: 32gb, 64gb, 128gb, 256gb, 512gb, 1tb, 2tb
const STORAGE_PATTERN = /^(\d+)(gb|tb)$/i;

// connectivity 토큰들
const CONNECTIVITY_TOKENS = ['wifi', 'cellular', 'lte', '5g'] as const;

// priceId에서 제거해야 할 오염 토큰 패턴 (끝에 붙은 순번)
const TRAILING_NUMBER_PATTERN = /-(\d{1,4})$/;

// ============================================================================
// 🔑 1) canonicalizeModelId - modelId 정규화 (READ 호환)
// ============================================================================
/**
 * 어떤 형식의 modelId든 canonical 형식으로 변환
 * 
 * 🔑 P2 핵심 규칙: + / ++ / plus 변형을 반드시 구분하여 보존!
 * - S25 → ...-s25
 * - S25+ / S25plus → ...-s25-plus
 * - S25++ → ...-s25-plus-plus
 * 
 * @example
 * canonicalizeModelId('smartphone-samsung-galaxy-s25') 
 *   → 'samsung-smartphone-galaxy-s-galaxy-s25'
 * canonicalizeModelId('samsung-smartphone-galaxy-s-galaxy-s25+') 
 *   → 'samsung-smartphone-galaxy-s-galaxy-s25-plus'
 * canonicalizeModelId('samsung-smartphone-galaxy-s-galaxy-s25++')
 *   → 'samsung-smartphone-galaxy-s-galaxy-s25-plus-plus'
 */
export function canonicalizeModelId(anyId: string): string {
  if (!anyId || typeof anyId !== 'string') {
    return '';
  }
  
  // 🔑 P2 핵심: + 기호를 먼저 정규화 토큰으로 변환 (유실 방지!)
  // 순서 중요: ++ 먼저 처리, 그 다음 + 처리
  let normalized = anyId
    .trim()
    // ++ → -plus-plus (단, 이미 plus-plus 형태면 건드리지 않음)
    .replace(/\+\+/g, '-plus-plus')
    // + → -plus (단, 이미 plus 형태면 건드리지 않음, plus-plus도 건드리지 않음)
    .replace(/\+(?!plus)/g, '-plus')
    // "플러스" 한글도 -plus로 정규화
    .replace(/플러스플러스/g, '-plus-plus')
    .replace(/플러스(?!-plus)/g, '-plus')
    // 연속 하이픈 정리
    .replace(/-+/g, '-')
    // 소문자 정규화
    .toLowerCase();
  
  // 1단계: 끝에 붙은 순번 제거 (예: -139, -1234)
  // 단, 세대 번호(airpods-1, airpods-pro-2 등)는 유지해야 함
  // 세대 번호 패턴: 제품명 뒤에 붙는 1~2자리 숫자
  const isGenerationNumber = (id: string): boolean => {
    // 🔑 SSOT: deviceCatalog.ts GENERATION_PATTERNS와 동기화 필수!
    // 웨어러블 세대 번호 + 스마트폰/태블릿 모델 번호 패턴
    const genPatterns = [
      // 웨어러블
      /airpods-\d$/,
      /airpods-pro-\d$/,
      /airpods-max$/,
      /watch-\d$/,
      /watch-ultra-?\d?$/,
      /buds-\d$/,
      /buds-pro-?\d?$/,
      // 스마트폰/태블릿 모델 번호 (trailing 숫자가 모델 식별에 필수!)
      /iphone-\d+$/i,
      /ipad-\d+$/i,
      /galaxy-s\d+$/i,
      /galaxy-z\d+$/i,
      /galaxy-a\d+$/i,
      /tab-s\d+$/i,
      /tab-a\d+$/i,
    ];
    return genPatterns.some(p => p.test(id));
  };
  
  // 순번 제거 (3자리 이상이면 확실히 순번)
  const trailingMatch = normalized.match(TRAILING_NUMBER_PATTERN);
  if (trailingMatch && trailingMatch[1].length >= 3) {
    normalized = normalized.replace(TRAILING_NUMBER_PATTERN, '');
  } else if (trailingMatch && !isGenerationNumber(normalized)) {
    // 2자리 이하도 세대 번호가 아니면 순번으로 간주하여 제거
    normalized = normalized.replace(TRAILING_NUMBER_PATTERN, '');
  }
  
  // 2단계: 끝에 붙은 region 제거 (예: -kr)
  normalized = normalized.replace(/-kr$/, '');
  
  // 3단계: 끝에 붙은 connectivity 토큰 제거 (예: -wifi, -cellular, -lte)
  normalized = normalized.replace(/-(wifi|cellular|lte|5g)$/, '');
  
  // 4단계: 끝에 붙은 storage 토큰 제거 (예: -256gb, -1tb)
  normalized = normalized.replace(/-(\d+)(gb|tb)$/, '');
  
  // 5단계: 중간에 있는 storage-connectivity-region 패턴도 제거
  // 예: samsung-tablet-...-256gb-wifi-kr-139 같은 오염 케이스
  normalized = normalized.replace(/-(\d+)(gb|tb)-(wifi|cellular|lte|5g)?(-kr)?(-\d+)?$/, '');
  
  // 6단계: 형식 정규화 (deviceType-brand → brand-deviceType)
  const parts = normalized.split('-').filter(p => p.length > 0);
  if (parts.length >= 2) {
    const first = parts[0];
    const second = parts[1];
    
    // deviceType-brand 형식이면 뒤집기
    if (DEVICE_TYPES.includes(first as any) && BRANDS.includes(second as any)) {
      // smartphone-samsung-... → samsung-smartphone-...
      return [second, first, ...parts.slice(2)].join('-');
    }
    
    // brand-deviceType 형식이면 그대로 (canonical)
    if (BRANDS.includes(first as any) && DEVICE_TYPES.includes(second as any)) {
      return parts.join('-');
    }
  }
  
  // 형식 판단 불가하면 원본 반환 (안전)
  return normalized;
}

// ============================================================================
// 🔑 2) parsePriceId - priceId 파싱 (READ 호환)
// ============================================================================
export interface ParsedPriceId {
  canonicalModelId: string;
  deviceType: DeviceType;
  storage?: string;
  connectivity?: Connectivity;
  region: Region;
  brand: 'Apple' | 'Samsung' | 'LG';
}

/**
 * priceId를 파싱하여 구성요소 추출
 * 레거시/신규 입력 모두 지원
 * 스마트폰 connectivity 누락 시 'lte'로 보정
 * 
 * @example
 * parsePriceId('samsung-smartphone-galaxy-s-galaxy-s25-256gb-lte-kr')
 *   → { canonicalModelId: 'samsung-smartphone-galaxy-s-galaxy-s25', 
 *       deviceType: 'smartphone', storage: '256gb', connectivity: 'lte', region: 'kr' }
 */
export function parsePriceId(anyPriceId: string): ParsedPriceId {
  if (!anyPriceId || typeof anyPriceId !== 'string') {
    throw new Error('parsePriceId: 유효하지 않은 priceId');
  }
  
  const normalized = anyPriceId.toLowerCase().trim();
  const parts = normalized.split('-').filter(p => p.length > 0);
  
  // 기본값
  let region: Region = 'kr';
  let storage: string | undefined;
  let connectivity: Connectivity | undefined;
  let deviceType: DeviceType = 'smartphone';
  let brand: 'Apple' | 'Samsung' | 'LG' = 'Samsung';
  
  // 마지막이 'kr'이면 region으로 확정하고 제거
  if (parts[parts.length - 1] === 'kr') {
    parts.pop();
  }
  
  // 뒤에서부터 connectivity/storage 찾기
  let modelIdEndIndex = parts.length;
  
  // connectivity 찾기 (뒤에서)
  for (let i = parts.length - 1; i >= 2; i--) {
    const part = parts[i];
    if (CONNECTIVITY_TOKENS.includes(part as any)) {
      if (part === 'lte' || part === '5g') {
        connectivity = 'lte'; // 5g도 lte로 정규화
      } else if (part === 'wifi') {
        connectivity = 'wifi';
      } else if (part === 'cellular') {
        connectivity = 'cellular';
      }
      modelIdEndIndex = i;
      break;
    }
  }
  
  // storage 찾기 (connectivity 앞에서)
  for (let i = modelIdEndIndex - 1; i >= 2; i--) {
    const part = parts[i];
    if (STORAGE_PATTERN.test(part)) {
      storage = part;
      modelIdEndIndex = i;
      break;
    }
  }
  
  // 남은 부분이 modelId
  const modelIdParts = parts.slice(0, modelIdEndIndex);
  
  // deviceType 및 brand 판별
  if (modelIdParts.length >= 2) {
    const first = modelIdParts[0];
    const second = modelIdParts[1];
    
    // brand-deviceType 형식
    if (BRANDS.includes(first as any) && DEVICE_TYPES.includes(second as any)) {
      brand = first === 'apple' ? 'Apple' : first === 'samsung' ? 'Samsung' : 'LG';
      deviceType = second as DeviceType;
    }
    // deviceType-brand 형식 (레거시)
    else if (DEVICE_TYPES.includes(first as any) && BRANDS.includes(second as any)) {
      deviceType = first as DeviceType;
      brand = second === 'apple' ? 'Apple' : second === 'samsung' ? 'Samsung' : 'LG';
    }
    // 형식 불명확 - 키워드로 추론
    else {
      const idStr = modelIdParts.join('-');
      brand = idStr.includes('apple') || idStr.includes('iphone') || idStr.includes('ipad')
        ? 'Apple'
        : idStr.includes('lg')
        ? 'LG'
        : 'Samsung';
      
      if (idStr.includes('tablet') || idStr.includes('ipad') || idStr.includes('tab-')) {
        deviceType = 'tablet';
      } else if (idStr.includes('wearable') || idStr.includes('airpods') || 
                 idStr.includes('watch') || idStr.includes('buds')) {
        deviceType = 'wearable';
      } else {
        deviceType = 'smartphone';
      }
    }
  }
  
  // canonical modelId 생성
  const canonicalModelId = canonicalizeModelId(modelIdParts.join('-'));
  
  // 디바이스 타입별 보정
  if (deviceType === 'smartphone') {
    // 스마트폰: connectivity 누락 시 'lte' 강제
    connectivity = 'lte';
  } else if (deviceType === 'wearable') {
    // 웨어러블: storage/connectivity 무조건 제거
    storage = undefined;
    connectivity = undefined;
  }
  // 태블릿: connectivity 누락은 그대로 undefined (저장 시 필수 체크에서 에러)
  
  return {
    canonicalModelId,
    deviceType,
    storage,
    connectivity,
    region,
    brand
  };
}

// ============================================================================
// 🔑 3) buildCanonicalPriceId - priceId 생성 (WRITE 전용)
// ============================================================================
export interface VariantInput {
  storage?: string;
  connectivity?: string;
}

/**
 * canonical priceId 생성 (WRITE 전용)
 * 스마트폰: connectivity = 'lte' 강제
 * 태블릿: connectivity 필수 (wifi/cellular)
 * 웨어러블: storage/connectivity 포함 금지
 * 
 * @throws storage/connectivity 규칙 위반 시 에러
 * 
 * @example
 * buildCanonicalPriceId('samsung-smartphone-galaxy-s25', 'smartphone', {storage: '256gb'})
 *   → 'samsung-smartphone-galaxy-s25-256gb-lte-kr'
 */
export function buildCanonicalPriceId(
  modelId: string,
  deviceType: DeviceType,
  variant: VariantInput,
  region: Region = 'kr'
): string {
  // modelId 정규화
  const canonicalModelId = canonicalizeModelId(modelId);
  
  if (!canonicalModelId) {
    throw new Error('buildCanonicalPriceId: 유효하지 않은 modelId');
  }
  
  const parts: string[] = [canonicalModelId];
  
  // 디바이스 타입별 규칙 적용
  switch (deviceType) {
    case 'smartphone': {
      // storage 필수
      if (!variant.storage) {
        throw new Error('buildCanonicalPriceId: 스마트폰은 storage가 필수입니다');
      }
      const normalizedStorage = variant.storage.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!STORAGE_PATTERN.test(normalizedStorage)) {
        throw new Error(`buildCanonicalPriceId: 유효하지 않은 storage 형식: ${variant.storage}`);
      }
      parts.push(normalizedStorage);
      
      // connectivity는 무조건 'lte' (입력값 무시)
      parts.push('lte');
      break;
    }
    
    case 'tablet': {
      // storage 필수
      if (!variant.storage) {
        throw new Error('buildCanonicalPriceId: 태블릿은 storage가 필수입니다');
      }
      const normalizedStorage = variant.storage.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!STORAGE_PATTERN.test(normalizedStorage)) {
        throw new Error(`buildCanonicalPriceId: 유효하지 않은 storage 형식: ${variant.storage}`);
      }
      parts.push(normalizedStorage);
      
      // connectivity 필수 + 정규화
      if (!variant.connectivity) {
        throw new Error('buildCanonicalPriceId: 태블릿은 connectivity가 필수입니다');
      }
      const connLower = variant.connectivity.toLowerCase().replace(/\s+/g, '');
      let normalizedConn: 'wifi' | 'cellular';
      
      if (connLower === 'wifi' || connLower === 'wi-fi') {
        normalizedConn = 'wifi';
      } else if (connLower.includes('cellular') || connLower.includes('lte') || connLower.includes('5g')) {
        normalizedConn = 'cellular';
      } else {
        throw new Error(`buildCanonicalPriceId: 태블릿 connectivity는 wifi 또는 cellular만 허용: ${variant.connectivity}`);
      }
      parts.push(normalizedConn);
      break;
    }
    
    case 'wearable': {
      // 구성(풀세트/유닛세트 등)을 storage로 포함
      if (variant.storage && variant.storage !== '기본') {
        const normalizedStorage = variant.storage.toLowerCase().replace(/[^a-z0-9가-힣]/g, '');
        if (normalizedStorage) {
          parts.push(normalizedStorage);
        }
      }
      // connectivity는 포함하지 않음
      break;
    }
    
    default:
      throw new Error(`buildCanonicalPriceId: 알 수 없는 deviceType: ${deviceType}`);
  }
  
  // region 추가 (항상 소문자 'kr')
  parts.push(region.toLowerCase() as Region);
  
  return parts.join('-');
}

// ============================================================================
// 🔑 유틸리티 함수들
// ============================================================================

/**
 * deviceType 판별 (modelId 또는 priceId에서)
 */
export function inferDeviceType(id: string): DeviceType {
  const lower = id.toLowerCase();
  
  // 명시적 deviceType 토큰 확인
  if (lower.includes('-smartphone-') || lower.startsWith('smartphone-')) {
    return 'smartphone';
  }
  if (lower.includes('-tablet-') || lower.startsWith('tablet-')) {
    return 'tablet';
  }
  if (lower.includes('-wearable-') || lower.startsWith('wearable-')) {
    return 'wearable';
  }
  
  // 제품명 키워드로 추론
  if (lower.includes('ipad') || lower.includes('tab-') || lower.includes('galaxy-tab')) {
    return 'tablet';
  }
  if (lower.includes('airpods') || lower.includes('watch') || lower.includes('buds')) {
    return 'wearable';
  }
  if (lower.includes('iphone') || lower.includes('galaxy-s') || lower.includes('galaxy-z') || 
      lower.includes('galaxy-a') || lower.includes('galaxy-note')) {
    return 'smartphone';
  }
  
  // 기본값
  return 'smartphone';
}

/**
 * brand 판별 (modelId 또는 priceId에서)
 */
export function inferBrand(id: string): 'Apple' | 'Samsung' | 'LG' {
  const lower = id.toLowerCase();

  if (lower.startsWith('apple-') || lower.includes('-apple-') ||
      lower.includes('iphone') || lower.includes('ipad') || lower.includes('airpods')) {
    return 'Apple';
  }

  if (lower.startsWith('lg-') || lower.includes('-lg-')) {
    return 'LG';
  }

  return 'Samsung';
}

/**
 * priceId가 canonical 형식인지 검증
 */
export function isCanonicalPriceId(priceId: string): boolean {
  try {
    const parsed = parsePriceId(priceId);
    const deviceType = inferDeviceType(priceId);
    
    // 재생성해서 동일한지 비교
    const rebuilt = buildCanonicalPriceId(
      parsed.canonicalModelId,
      deviceType,
      { storage: parsed.storage, connectivity: parsed.connectivity },
      parsed.region
    );
    
    return priceId.toLowerCase() === rebuilt;
  } catch {
    return false;
  }
}

/**
 * 스마트폰 connectivity 정규화 (항상 'LTE' 반환)
 */
export function normalizeSmartphoneConnectivity(_input?: string): 'LTE' {
  return 'LTE';
}

/**
 * 태블릿 connectivity 정규화 ('Wi-Fi' 또는 'Wi-Fi + Cellular' 반환)
 */
export function normalizeTabletConnectivity(input?: string): 'Wi-Fi' | 'Wi-Fi + Cellular' {
  if (!input) return 'Wi-Fi';
  
  const lower = input.toLowerCase().replace(/\s+/g, '');
  
  if (lower.includes('cellular') || lower.includes('lte') || lower.includes('5g')) {
    return 'Wi-Fi + Cellular';
  }
  
  return 'Wi-Fi';
}

/**
 * storage 정규화 (소문자, 숫자+단위만)
 */
export function normalizeStorage(input?: string): string | undefined {
  if (!input || input === '기본') return undefined;
  
  const normalized = input.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  if (STORAGE_PATTERN.test(normalized)) {
    return normalized;
  }
  
  return undefined;
}

// ============================================================================
// 🔑 검증 함수들 (저장 전 사용)
// ============================================================================

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * priceId와 payload 데이터의 일치 검증 (저장 전 필수)
 */
export function validatePriceIdConsistency(
  priceId: string,
  payload: {
    modelId?: string;
    deviceType?: DeviceType;
    storage?: string;
    connectivity?: string;
  }
): ValidationResult {
  const errors: string[] = [];
  
  try {
    const parsed = parsePriceId(priceId);
    
    // modelId 일치 검증
    if (payload.modelId) {
      const payloadCanonicalModelId = canonicalizeModelId(payload.modelId);
      if (parsed.canonicalModelId !== payloadCanonicalModelId) {
        errors.push(`modelId 불일치: priceId에서 파싱=${parsed.canonicalModelId}, payload=${payloadCanonicalModelId}`);
      }
    }
    
    // deviceType 일치 검증
    if (payload.deviceType && parsed.deviceType !== payload.deviceType) {
      errors.push(`deviceType 불일치: priceId에서 파싱=${parsed.deviceType}, payload=${payload.deviceType}`);
    }
    
    // 디바이스 타입별 규칙 검증
    const deviceType = payload.deviceType || parsed.deviceType;
    
    if (deviceType === 'smartphone') {
      // 스마트폰: connectivity는 항상 'lte'
      if (parsed.connectivity !== 'lte') {
        errors.push(`스마트폰 connectivity는 'lte'여야 함: 실제=${parsed.connectivity}`);
      }
    } else if (deviceType === 'tablet') {
      // 태블릿: connectivity 필수 (wifi 또는 cellular)
      if (!parsed.connectivity || !['wifi', 'cellular'].includes(parsed.connectivity)) {
        errors.push(`태블릿 connectivity는 'wifi' 또는 'cellular'여야 함: 실제=${parsed.connectivity}`);
      }
    } else if (deviceType === 'wearable') {
      // 웨어러블: storage/connectivity 없어야 함
      if (parsed.storage) {
        errors.push(`웨어러블은 storage를 포함할 수 없음: 실제=${parsed.storage}`);
      }
      if (parsed.connectivity) {
        errors.push(`웨어러블은 connectivity를 포함할 수 없음: 실제=${parsed.connectivity}`);
      }
    }
    
    // storage 일치 검증 (웨어러블 제외)
    if (deviceType !== 'wearable' && payload.storage) {
      const payloadStorage = normalizeStorage(payload.storage);
      if (parsed.storage !== payloadStorage) {
        errors.push(`storage 불일치: priceId에서 파싱=${parsed.storage}, payload=${payloadStorage}`);
      }
    }
    
  } catch (e) {
    errors.push(`priceId 파싱 실패: ${e instanceof Error ? e.message : String(e)}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

// ============================================================================
// 🔑 P2 회귀 방지: + 변형 모델 ID 검증 유틸리티
// ============================================================================
/**
 * S25 / S25+ / S25++ 같은 + 변형 모델들이 서로 다른 ID를 갖는지 검증
 * 
 * @example
 * validatePlusVariantsDistinct(['Galaxy S25', 'Galaxy S25+', 'Galaxy S25++'])
 *   → { isValid: true, details: [...] } if all have distinct IDs
 *   → { isValid: false, duplicates: [...] } if any IDs collide
 */
export function validatePlusVariantsDistinct(
  modelNames: string[]
): { isValid: boolean; details: { name: string; id: string }[]; duplicates?: string[] } {
  const details: { name: string; id: string }[] = [];
  const idMap = new Map<string, string[]>();
  
  modelNames.forEach(name => {
    // 모델명에서 canonical ID 생성 (generateModelId 시뮬레이션)
    const normalizedName = name
      .trim()
      .replace(/\+\+/g, '-plus-plus')
      .replace(/\+/g, '-plus')
      .replace(/플러스플러스/gi, '-plus-plus')
      .replace(/플러스/gi, '-plus')
      .replace(/\s+/g, '-')
      .toLowerCase()
      .replace(/[^a-z0-9\-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    details.push({ name, id: normalizedName });
    
    const existing = idMap.get(normalizedName) || [];
    existing.push(name);
    idMap.set(normalizedName, existing);
  });
  
  const duplicates: string[] = [];
  idMap.forEach((names, id) => {
    if (names.length > 1) {
      duplicates.push(`ID "${id}" → 충돌 모델: [${names.join(', ')}]`);
    }
  });
  
  if (duplicates.length > 0) {
    console.error(`🚨 [P2 회귀 감지] + 변형 모델 ID 충돌 발생!`);
    console.error(`   `, duplicates);
    return { isValid: false, details, duplicates };
  }
  
  console.log(`✅ [P2 검증 통과] + 변형 모델 ID 구분 정상:`);
  details.forEach(d => console.log(`   "${d.name}" → "${d.id}"`));
  return { isValid: true, details };
}

/**
 * 특정 모델 ID가 + 변형을 올바르게 포함하는지 검증
 * 
 * @example
 * assertPlusPreserved('Galaxy S25++', 'samsung-smartphone-galaxy-s-galaxy-s25-plus-plus')
 *   → true (ID에 plus-plus 포함)
 * assertPlusPreserved('Galaxy S25++', 'samsung-smartphone-galaxy-s-galaxy-s25')
 *   → false (plus-plus 누락!)
 */
export function assertPlusPreserved(modelName: string, modelId: string): boolean {
  const nameLower = modelName.toLowerCase();
  const idLower = modelId.toLowerCase();
  
  // ++ 가 있으면 ID에 plus-plus 가 있어야 함
  if (nameLower.includes('++') || nameLower.includes('플러스플러스')) {
    if (!idLower.includes('plus-plus')) {
      console.error(`🚨 [P2 회귀 감지] "${modelName}"에 ++ 가 있지만 ID "${modelId}"에 plus-plus 가 없음!`);
      return false;
    }
  }
  // + 가 있으면 (++ 제외) ID에 plus 가 있어야 함
  else if (nameLower.includes('+') || nameLower.includes('플러스')) {
    if (!idLower.includes('plus')) {
      console.error(`🚨 [P2 회귀 감지] "${modelName}"에 + 가 있지만 ID "${modelId}"에 plus 가 없음!`);
      return false;
    }
  }
  // + 가 없으면 ID에도 plus 가 없어야 함 (S25 ≠ S25+)
  else {
    if (idLower.includes('plus')) {
      console.error(`🚨 [P2 회귀 감지] "${modelName}"에 + 가 없는데 ID "${modelId}"에 plus 가 있음!`);
      return false;
    }
  }
  
  return true;
}

// 📊 디버깅/로깅용 유틸리티
export function debugPriceId(priceId: string): void {
  console.log('🔍 [idCanonical] priceId 디버그:');
  console.log(`   입력: ${priceId}`);
  
  try {
    const parsed = parsePriceId(priceId);
    console.log(`   파싱 결과:`, parsed);
    
    const deviceType = inferDeviceType(priceId);
    console.log(`   추론된 deviceType: ${deviceType}`);
    
    const canonical = buildCanonicalPriceId(
      parsed.canonicalModelId,
      deviceType,
      { storage: parsed.storage, connectivity: parsed.connectivity },
      parsed.region
    );
    console.log(`   재생성된 canonical: ${canonical}`);
    console.log(`   일치 여부: ${priceId.toLowerCase() === canonical}`);
  } catch (e) {
    console.error(`   에러:`, e);
  }
}

